import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { DocumentStorage, DocumentStorageFilter, DocumentStorageFormData } from '../types/documentStorage';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export function useDocumentStorage(filters: DocumentStorageFilter = {}) {
  const [documents, setDocuments] = useState<DocumentStorage[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    let q = query(
      collection(db, 'documentStorage'),
      where('organizationId', '==', user.organizationId),
      orderBy('createdAt', 'desc')
    );

    // Apply filters
    if (filters.customerId) {
      q = query(q, where('customerId', '==', filters.customerId));
    }
    
    // Mappa szűrés logika:
    // - Ha filters.folderId === undefined: minden dokumentum (beleértve null mappájúakat)
    // - Ha filters.folderId === null: csak a gyökér dokumentumok (folderId === null)
    // - Ha filters.folderId === string: csak az adott mappa dokumentumai
    if (filters.folderId !== undefined) {
      q = query(q, where('folderId', '==', filters.folderId));
    }
    
    if (filters.categoryId) {
      q = query(q, where('category.id', '==', filters.categoryId));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const documentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        creationDate: doc.data().creationDate?.toDate() || null,
        expirationDate: doc.data().expirationDate?.toDate() || null,
      } as DocumentStorage));

      // Apply client-side filters
      let filteredDocuments = documentsData;

      // Hierarchical folder filtering (client-side) - Windows-style
      if (filters.folderIds && filters.folderIds.length > 0) {
        filteredDocuments = filteredDocuments.filter(doc => {
          // Ha a dokumentum mappában van, és az a mappa a szűrőben szerepel
          if (doc.folderId && filters.folderIds!.includes(doc.folderId)) {
            return true;
          }
          // Ha a dokumentum gyökérben van (folderId === null) és 'null' szerepel a szűrőben
          if (!doc.folderId && filters.folderIds!.includes('null')) {
            return true;
          }
          return false;
        });
      }

      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        filteredDocuments = filteredDocuments.filter(doc =>
          doc.title?.toLowerCase().includes(searchLower) ||
          doc.description?.toLowerCase().includes(searchLower) ||
          doc.customerName?.toLowerCase().includes(searchLower) ||
          doc.tags?.some(tag => tag?.toLowerCase().includes(searchLower))
        );
      }

      if (filters.tags && filters.tags.length > 0) {
        filteredDocuments = filteredDocuments.filter(doc =>
          filters.tags!.some(tag => doc.tags?.includes(tag))
        );
      }

      if (filters.dateFrom) {
        filteredDocuments = filteredDocuments.filter(doc =>
          doc.createdAt >= filters.dateFrom!
        );
      }

      if (filters.dateTo) {
        filteredDocuments = filteredDocuments.filter(doc =>
          doc.createdAt <= filters.dateTo!
        );
      }

      setDocuments(filteredDocuments);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching document storage:', error);
      toast.error('Hiba a dokumentumok betöltésekor');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [
    user?.organizationId, 
    filters.customerId, 
    filters.folderId, 
    filters.folderIds, 
    filters.categoryId, 
    filters.status, 
    filters.searchTerm, 
    filters.tags, 
    filters.dateFrom, 
    filters.dateTo
  ]);

  const uploadFile = async (file: File, customerId: string): Promise<string> => {
    if (!user?.organizationId) throw new Error('No organization');

    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const filePath = `documentStorage/${user.organizationId}/${customerId}/${fileName}`;
    
    const storageRef = ref(storage, filePath);
    
    try {
      const snapshot = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(snapshot.ref);
      return downloadUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw new Error('Fájl feltöltése sikertelen');
    }
  };

  const createDocument = async (data: DocumentStorageFormData): Promise<string> => {
    if (!user?.organizationId || !user.uid) {
      throw new Error('Authentication required');
    }

    try {
      let downloadUrl = '';
      let fileData = {};

      // Upload file if provided
      if (data.file) {
        downloadUrl = await uploadFile(data.file, data.customerId);
        fileData = {
          fileName: data.file.name,
          fileSize: data.file.size,
          fileType: data.file.type,
          filePath: downloadUrl,
          downloadUrl
        };
      }

      // Get customer and category data to store with the document
      let customerDoc = null;
      let customerName = '';
      
      // Only fetch customer if customerId is provided and not 'general'
      if (data.customerId && data.customerId !== 'general') {
        customerDoc = await getDoc(doc(db, 'customers', data.customerId));
        if (customerDoc.exists()) {
          const customerData = customerDoc.data();
          if (customerData.type === 'organization') {
            customerName = customerData.companyName || '';
          } else {
            customerName = `${customerData.firstName || ''} ${customerData.lastName || ''}`.trim();
          }
        }
      }
      
      const categoryDoc = await getDoc(doc(db, 'documentStorageCategories', data.categoryId));
      const categoryData = categoryDoc.exists() ? categoryDoc.data() : null;

      const docRef = await addDoc(collection(db, 'documentStorage'), {
        title: data.title,
        description: data.description || '',
        categoryId: data.categoryId,
        category: categoryData ? {
          id: data.categoryId,
          name: categoryData.name,
          color: categoryData.color,
          icon: categoryData.icon
        } : null,
        customerId: data.customerId && data.customerId !== 'general' ? data.customerId : '',
        customerName,
        folderId: data.folderId || null,
        tags: data.tags || [],
        isPublic: data.isPublic,
        creationDate: data.creationDate || new Date(),
        expirationDate: data.expirationDate || null,
        version: 1,
        status: 'active',
        createdBy: user.uid,
        createdByName: user.displayName || user.email || '',
        organizationId: user.organizationId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...fileData
      });

      toast.success('Dokumentum sikeresen létrehozva');
      return docRef.id;
    } catch (error) {
      console.error('Error creating document storage:', error);
      throw new Error('Dokumentum létrehozása sikertelen');
    }
  };

  const updateDocument = async (id: string, data: Partial<DocumentStorageFormData>): Promise<void> => {
    if (!user?.organizationId) {
      throw new Error('Authentication required');
    }

    try {
      let updateData: any = { ...data };
      delete updateData.file; // Remove file from the update data

      // Handle file upload if new file is provided
      if (data.file && data.customerId) {
        const downloadUrl = await uploadFile(data.file, data.customerId);
        updateData = {
          ...updateData,
          fileName: data.file.name,
          fileSize: data.file.size,
          fileType: data.file.type,
          filePath: downloadUrl,
          downloadUrl
        };
      }

      // Clean undefined values from updateData
      const cleanUpdateData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      );

      const docRef = doc(db, 'documentStorage', id);
      await updateDoc(docRef, {
        ...cleanUpdateData,
        updatedAt: serverTimestamp()
      });

      toast.success('Dokumentum sikeresen frissítve');
    } catch (error) {
      console.error('Error updating document storage:', error);
      throw new Error('Dokumentum frissítése sikertelen');
    }
  };

  const removeFile = async (id: string): Promise<void> => {
    if (!user?.organizationId) {
      throw new Error('Authentication required');
    }

    try {
      const docRef = doc(db, 'documentStorage', id);
      await updateDoc(docRef, {
        fileName: null,
        fileSize: null,
        fileType: null,
        filePath: null,
        downloadUrl: null,
        updatedAt: serverTimestamp()
      });

      toast.success('Fájl sikeresen eltávolítva');
    } catch (error) {
      console.error('Error removing file:', error);
      throw new Error('Fájl eltávolítása sikertelen');
    }
  };

  const deleteDocument = async (id: string): Promise<void> => {
    if (!user?.organizationId) {
      throw new Error('Authentication required');
    }

    try {
      // Find the document to get file path for deletion
      const document = documents.find(doc => doc.id === id);
      
      // Delete file from storage if exists
      if (document?.filePath) {
        try {
          const fileRef = ref(storage, document.filePath);
          await deleteObject(fileRef);
        } catch (fileError) {
          console.warn('Could not delete file from storage:', fileError);
        }
      }

      // Delete document from Firestore
      await deleteDoc(doc(db, 'documentStorage', id));
      
      toast.success('Dokumentum sikeresen törölve');
    } catch (error) {
      console.error('Error deleting document storage:', error);
      throw new Error('Dokumentum törlése sikertelen');
    }
  };

  const archiveDocument = async (id: string): Promise<void> => {
    await updateDocument(id, { status: 'archived' } as any);
  };

  const restoreDocument = async (id: string): Promise<void> => {
    await updateDocument(id, { status: 'active' } as any);
  };

  return {
    documents,
    loading,
    createDocument,
    updateDocument,
    deleteDocument,
    removeFile,
    archiveDocument,
    restoreDocument
  };
}
